import React, { useState, useEffect } from "react";
import {
  Box, Typography, Stack, Card, CardContent, Grid, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, InputAdornment, Alert, Avatar, Divider,
  FormControlLabel, Checkbox, CircularProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  collection, onSnapshot, doc, setDoc, deleteDoc,
  serverTimestamp, query, where, getDocs, updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { createAuthUser } from "../firebase/user";

interface PortalUser {
  id: string; uid: string; email: string; displayName?: string;
  documentType?: string; documentNumber?: string; phone?: string;
  role: "client" | "admin"; isAdmin?: boolean; createdAt?: any;
  contactProfile?: {
    documentType?: string; documentNumber?: string;
    phone?: string; clientName?: string;
  };
}
interface CreditCase {
  id: string; code: string; clientName: string; status: string; amount: number;
}
const defaultNewUser = {
  email: "", password: "", displayName: "", documentType: "CC",
  documentNumber: "", phone: "", isAdmin: false,
};

function getDocNumber(u: PortalUser) { return u.documentNumber || u.contactProfile?.documentNumber || ""; }
function getDocType(u: PortalUser) { return u.documentType || u.contactProfile?.documentType || "CC"; }
function getPhone(u: PortalUser) { return u.phone || u.contactProfile?.phone || ""; }
function getName(u: PortalUser) { return u.displayName || u.contactProfile?.clientName || ""; }

export default function DistripakoUsuariosPage() {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  const [newUser, setNewUser] = useState(defaultNewUser);
  const [selectedUser, setSelectedUser] = useState<PortalUser | null>(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [userCredits, setUserCredits] = useState<CreditCase[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalUser | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ documentType: "CC", documentNumber: "", phone: "", displayName: "" });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<PortalUser, "id">) }))
        .filter((u) => u.email);
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setUsers(list);
      setLoading(false);
    }, (err) => { console.error(err); setLoading(false); });
    return () => unsub();
  }, []);

  const loadUserCredits = async (uid: string) => {
    setLoadingCredits(true);
    try {
      const q = query(collection(db, "credit_cases"), where("userId", "==", uid));
      const snap = await getDocs(q);
      setUserCredits(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CreditCase, "id">) })));
    } catch (e) { console.error(e); }
    finally { setLoadingCredits(false); }
  };

  const handleOpenDetail = (user: PortalUser) => {
    setSelectedUser(user); setOpenDetail(true);
    setEditMode(false); setSaveSuccess(false);
    setEditData({
      documentType: getDocType(user),
      documentNumber: getDocNumber(user),
      phone: getPhone(user),
      displayName: getName(user),
    });
    loadUserCredits(user.uid || user.id);
  };

  const handleSaveContactData = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", selectedUser.id), {
        documentType: editData.documentType,
        documentNumber: editData.documentNumber,
        phone: editData.phone,
        displayName: editData.displayName || selectedUser.displayName || "",
        contactProfile: {
          documentType: editData.documentType,
          documentNumber: editData.documentNumber,
          phone: editData.phone,
          clientName: editData.displayName || selectedUser.displayName || "",
          updatedAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      });
      setSaveSuccess(true);
      setEditMode(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving user data:", err);
      alert("Error al guardar. Intenta de nuevo.");
    } finally { setSaving(false); }
  };

  const handleCloseCreate = () => {
    setOpenCreate(false); setNewUser(defaultNewUser);
    setCreateError(""); setCreateSuccess("");
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.email.trim()) {
      setCreateError("El correo electrónico es obligatorio.");
      return;
    }
    const cleanEmail = newUser.email.trim().toLowerCase();
    const hasPassword = Boolean(newUser.password && newUser.password.trim());

    if (hasPassword && newUser.password.trim().length < 6) {
      setCreateError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setCreating(true);
    setCreateError("");
    setCreateSuccess("");

    try {
      if (hasPassword) {
        // Creación con Firebase Auth (correo y contraseña)
        const uid = await createAuthUser(cleanEmail, newUser.password.trim());
        await setDoc(doc(db, "users", uid), {
          uid,
          email: cleanEmail,
          displayName: newUser.displayName.trim() || null,
          documentType: newUser.documentType || "CC",
          documentNumber: newUser.documentNumber.trim() || null,
          cedula: newUser.documentNumber.trim() || null,
          documento: newUser.documentNumber.trim() || null,
          phone: newUser.phone.trim() || null,
          telefono: newUser.phone.trim() || null,
          celular: newUser.phone.trim() || null,
          role: newUser.isAdmin ? "admin" : "client",
          isAdmin: newUser.isAdmin,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          contactProfile: {
            documentType: newUser.documentType || "CC",
            documentNumber: newUser.documentNumber.trim() || null,
            phone: newUser.phone.trim() || null,
            clientName: newUser.displayName.trim() || null,
            email: cleanEmail,
            updatedAt: serverTimestamp(),
          },
        }, { merge: true });
        setCreateSuccess(`✅ Usuario "${cleanEmail}" creado con contraseña.`);
      } else {
        // Precarga de cliente para acceso con Google (sin contraseña previa)
        // 1. Verificar si ya existe un documento con este correo
        const q = query(collection(db, "users"), where("email", "==", cleanEmail));
        const snap = await getDocs(q);

        let targetRef;
        if (!snap.empty) {
          targetRef = snap.docs[0].ref;
        } else {
          targetRef = doc(collection(db, "users"));
        }

        await setDoc(targetRef, {
          uid: targetRef.id,
          email: cleanEmail,
          displayName: newUser.displayName.trim() || null,
          documentType: newUser.documentType || "CC",
          documentNumber: newUser.documentNumber.trim() || null,
          cedula: newUser.documentNumber.trim() || null,
          documento: newUser.documentNumber.trim() || null,
          phone: newUser.phone.trim() || null,
          telefono: newUser.phone.trim() || null,
          celular: newUser.phone.trim() || null,
          role: newUser.isAdmin ? "admin" : "client",
          isAdmin: newUser.isAdmin,
          isPreloaded: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          contactProfile: {
            documentType: newUser.documentType || "CC",
            documentNumber: newUser.documentNumber.trim() || null,
            phone: newUser.phone.trim() || null,
            clientName: newUser.displayName.trim() || null,
            email: cleanEmail,
            updatedAt: serverTimestamp(),
          },
        }, { merge: true });

        setCreateSuccess(`✅ Cliente "${cleanEmail}" precargado correctamente. Al ingresar con Google se sincronizarán su C.C. y teléfono.`);
      }

      setTimeout(() => handleCloseCreate(), 1800);
    } catch (err: any) {
      if (err.code === "auth/email-already-in-use") {
        setCreateError("Ya existe una cuenta con este correo en Firebase Auth.");
      } else {
        setCreateError("Error al guardar: " + (err.message || err.code));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "users", deleteTarget.id));
      setOpenDelete(false); setDeleteTarget(null);
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchTerm.trim()) return true;
    const t = searchTerm.toLowerCase();
    return u.email?.toLowerCase().includes(t) || u.displayName?.toLowerCase().includes(t) ||
      getDocNumber(u).toLowerCase().includes(t) || getPhone(u).toLowerCase().includes(t);
  });

  const adminCount = users.filter((u) => u.isAdmin || u.role === "admin").length;
  const clientCount = users.filter((u) => !u.isAdmin && u.role !== "admin").length;
  const userInitials = (u: PortalUser) => (getName(u) || u.email || "U").substring(0, 2).toUpperCase();

  return (
    <Box p={{ xs: 2, md: 3 }} bgcolor="#f8fafc" minHeight="100vh">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={900}>Gestión de Usuarios del Portal</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Crea y administra los usuarios que pueden acceder al portal de clientes
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenCreate(true)} sx={{ fontWeight: 700, borderRadius: 2.5, px: 3 }}>
          Nuevo Usuario
        </Button>
      </Stack>

      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Usuarios", value: users.length, icon: <GroupIcon fontSize="large" />, color: "#3b82f6", bg: "#eff6ff" },
          { label: "Clientes", value: clientCount, icon: <PersonIcon fontSize="large" />, color: "#10b981", bg: "#ecfdf5" },
          { label: "Administradores", value: adminCount, icon: <AdminPanelSettingsIcon fontSize="large" />, color: "#8b5cf6", bg: "#f5f3ff" },
        ].map((kpi) => (
          <Grid size={{ xs: 12, sm: 4 }} key={kpi.label}>
            <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={1}>{kpi.label}</Typography>
                    <Typography variant="h4" fontWeight={900} color={kpi.color}>{kpi.value}</Typography>
                  </Box>
                  <Box sx={{ color: kpi.color, bgcolor: kpi.bg, borderRadius: 3, p: 1.5, display: "flex" }}>{kpi.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={800}>Directorio de Usuarios</Typography>
            <TextField size="small" placeholder="Buscar por nombre, email, documento..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Stack>
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["USUARIO", "DOCUMENTO / CÉDULA", "TELÉFONO", "ROL", "REGISTRADO", "ACCIONES"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 800, fontSize: "0.75rem" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>Cargando usuarios...</TableCell></TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    {searchTerm ? "No se encontraron usuarios" : "No hay usuarios registrados"}
                  </TableCell></TableRow>
                ) : filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: user.isAdmin ? "#8b5cf6" : "#10b981", fontSize: "0.85rem", fontWeight: 900 }}>
                          {userInitials(user)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{getName(user) || "Sin nombre"}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {getDocNumber(user)
                        ? <Typography variant="body2" fontWeight={600}>{getDocType(user)} {getDocNumber(user)}</Typography>
                        : <Chip label="Sin cédula" size="small" variant="outlined" sx={{ fontSize: "0.68rem", color: "text.disabled", borderColor: "divider" }} />}
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{getPhone(user) || "—"}</Typography></TableCell>
                    <TableCell>
                      <Chip label={user.isAdmin || user.role === "admin" ? "Admin" : "Cliente"} size="small"
                        color={user.isAdmin || user.role === "admin" ? "secondary" : "success"}
                        sx={{ fontWeight: 700, fontSize: "0.7rem" }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString("es-CO") : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5}>
                        <Tooltip title="Ver detalles y editar"><IconButton size="small" onClick={() => handleOpenDetail(user)} color="primary"><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar perfil"><IconButton size="small" onClick={() => { setDeleteTarget(user); setOpenDelete(true); }} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={openCreate} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Crear / Precargar Cliente del Portal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              💡 <strong>Acceso con Google:</strong> Si el cliente iniciará sesión con Google en el portal, solo necesitas ingresar su <strong>Nombre</strong>, <strong>Cédula</strong>, <strong>Teléfono</strong> y su <strong>Correo Gmail</strong>. Deja la contraseña en blanco. Cuando el cliente entre por primera vez con Google, sus datos se vincularán automáticamente.
            </Alert>

            {createError && <Alert severity="error">{createError}</Alert>}
            {createSuccess && <Alert severity="success">{createSuccess}</Alert>}

            <TextField label="Nombre Completo *" value={newUser.displayName}
              onChange={(e) => setNewUser((p) => ({ ...p, displayName: e.target.value }))} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }} />
            
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 4 }}>
                <TextField select label="Tipo Doc." value={newUser.documentType}
                  onChange={(e) => setNewUser((p) => ({ ...p, documentType: e.target.value }))}
                  fullWidth SelectProps={{ native: true }}>
                  {["CC", "CE", "NIT", "PA"].map((t) => <option key={t} value={t}>{t}</option>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 8 }}>
                <TextField label="Cédula / Número de Documento *" value={newUser.documentNumber}
                  onChange={(e) => setNewUser((p) => ({ ...p, documentNumber: e.target.value }))} fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" /></InputAdornment> }} />
              </Grid>
            </Grid>

            <TextField label="Correo Electrónico (Gmail del Cliente) *" type="email" value={newUser.email}
              onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }} />

            <TextField label="Teléfono / WhatsApp" value={newUser.phone}
              onChange={(e) => setNewUser((p) => ({ ...p, phone: e.target.value }))} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }} />

            <TextField label="Contraseña (Opcional - solo si NO usa Google)" type="password" value={newUser.password}
              onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))} fullWidth
              helperText="Opcional. Déjala vacía si el usuario usará el botón de Google en el portal." />

            <FormControlLabel
              control={<Checkbox checked={newUser.isAdmin} onChange={(e) => setNewUser((p) => ({ ...p, isAdmin: e.target.checked }))} />}
              label="Es Administrador del sistema" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseCreate} disabled={creating}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={creating} sx={{ fontWeight: 700 }}>
            {creating ? "Guardando..." : (newUser.password ? "Crear con Contraseña" : "Guardar / Precargar Cliente")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={openDetail} onClose={() => { setOpenDetail(false); setEditMode(false); }} fullWidth maxWidth="sm">
        {selectedUser && (
          <>
            <DialogTitle sx={{ fontWeight: 900, pb: 0 }}>
              <Stack direction="row" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: selectedUser.isAdmin ? "#8b5cf6" : "#10b981", fontWeight: 900 }}>
                  {userInitials(selectedUser)}
                </Avatar>
                <Box flex={1}>
                  <Typography fontWeight={900}>{getName(selectedUser) || "Sin nombre"}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedUser.email}</Typography>
                </Box>
                <Tooltip title={editMode ? "Cancelar edición" : "Editar cédula y datos de contacto"}>
                  <IconButton onClick={() => { setEditMode((v) => !v); setSaveSuccess(false); }} color={editMode ? "error" : "primary"} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Stack spacing={2}>
                {saveSuccess && <Alert severity="success" sx={{ borderRadius: 2 }}>✅ Datos guardados correctamente. Se reflejan en el portal del cliente.</Alert>}

                {editMode ? (
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 2, bgcolor: "#f0f9ff", borderColor: "#bae6fd" }}>
                    <Typography variant="caption" color="primary" fontWeight={800} textTransform="uppercase" letterSpacing={0.5} display="block" mb={2}>
                      ✏️ Editar Cédula y Datos de Contacto
                    </Typography>
                    <Stack spacing={2}>
                      <TextField label="Nombre Completo" value={editData.displayName}
                        onChange={(e) => setEditData((p) => ({ ...p, displayName: e.target.value }))} fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }} />
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 4 }}>
                          <TextField select label="Tipo" value={editData.documentType}
                            onChange={(e) => setEditData((p) => ({ ...p, documentType: e.target.value }))}
                            fullWidth size="small" SelectProps={{ native: true }}>
                            {["CC", "CE", "NIT", "PA"].map((t) => <option key={t} value={t}>{t}</option>)}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                          <TextField label="Cédula / Número de Documento" value={editData.documentNumber}
                            onChange={(e) => setEditData((p) => ({ ...p, documentNumber: e.target.value }))} fullWidth size="small"
                            InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                      </Grid>
                      <TextField label="Teléfono / WhatsApp" value={editData.phone}
                        onChange={(e) => setEditData((p) => ({ ...p, phone: e.target.value }))} fullWidth size="small"
                        InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }} />
                      <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveContactData} disabled={saving} sx={{ fontWeight: 700, borderRadius: 2 }}>
                        {saving ? "Guardando en Firebase..." : "Guardar Cambios"}
                      </Button>
                    </Stack>
                  </Card>
                ) : (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Grid container spacing={1.5}>
                        {[
                          { icon: <EmailIcon fontSize="small" />, label: "Email", val: selectedUser.email },
                          { icon: <BadgeIcon fontSize="small" />, label: "Documento / Cédula", val: getDocNumber(selectedUser) ? `${getDocType(selectedUser)} ${getDocNumber(selectedUser)}` : "—" },
                          { icon: <PhoneIcon fontSize="small" />, label: "Teléfono", val: getPhone(selectedUser) || "—" },
                          { icon: <AdminPanelSettingsIcon fontSize="small" />, label: "Rol", val: selectedUser.isAdmin ? "Administrador" : "Cliente" },
                        ].map((r) => (
                          <Grid size={{ xs: 12, sm: 6 }} key={r.label}>
                            <Stack direction="row" alignItems="flex-start" gap={1}>
                              <Box sx={{ color: "text.secondary", mt: 0.2 }}>{r.icon}</Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} display="block">{r.label}</Typography>
                                <Typography variant="body2" fontWeight={600}>{r.val}</Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, p: 1.5, border: "1px dashed #e2e8f0" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>UID Firebase Auth</Typography>
                  <Typography variant="caption" fontFamily="monospace" display="block" sx={{ wordBreak: "break-all", mt: 0.5 }}>{selectedUser.uid || selectedUser.id}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                    <CreditCardIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={800}>Créditos Vinculados</Typography>
                  </Stack>
                  {loadingCredits ? (
                    <Typography variant="body2" color="text.secondary">Cargando créditos...</Typography>
                  ) : userCredits.length === 0 ? (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>Este usuario no tiene créditos vinculados aún.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {userCredits.map((c) => (
                        <Card key={c.id} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2" fontWeight={800}>{c.code}</Typography>
                                <Typography variant="caption" color="text.secondary">${Number(c.amount).toLocaleString("es-CO")}</Typography>
                              </Box>
                              <Chip label={c.status} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: "0.68rem" }} />
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => { setOpenDetail(false); setEditMode(false); }} sx={{ fontWeight: 700 }}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle sx={{ fontWeight: 900 }}>¿Eliminar usuario?</DialogTitle>
        <DialogContent>
          <Typography>Se eliminará el perfil de <strong>{deleteTarget?.email}</strong> de Firestore.</Typography>
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>La cuenta en Firebase Auth debe eliminarse manualmente desde la consola de Firebase.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} disabled={deleting} sx={{ fontWeight: 700 }}>
            {deleting ? "Eliminando..." : "Eliminar Perfil"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
